import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { settingsAPI } from '../services/api';



import { TIMEZONE_DATA, COUNTRIES } from '../utils/constants';

// ─── Components ───

const Toggle = ({ checked, onChange }) => (
    <button
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-300 focus:outline-none ${checked ? 'bg-primary dark:bg-cyan' : 'bg-gray-300 dark:bg-gray-600'}`}
    >
        <span
            className={`inline-block h-5 w-5 rounded-full bg-white shadow-md transform transition-transform duration-300 ${checked ? 'translate-x-5' : 'translate-x-0.5'}`}
        />
    </button>
);

const SettingsSection = ({ title, subtitle, children }) => (
    <div className="bg-mint dark:bg-navy p-0 md:p-4 rounded-xl mb-6">
        <div className="mb-6">
            <h3 className="text-xl font-bold text-darkTeal dark:text-light mb-1">{title}</h3>
            {subtitle && <p className="text-sm text-darkTeal/50 dark:text-light/50">{subtitle}</p>}
        </div>
        <div className="grid grid-cols-1 gap-6">
            {children}
        </div>
    </div>
);

const NavItem = ({ id, icon, label, description, activeTab, setActiveTab }) => (
    <button
        onClick={() => setActiveTab(id)}
        className={`w-full text-left p-4 rounded-xl transition-all duration-300 flex items-center gap-4 group relative overflow-hidden ${activeTab === id
            ? 'bg-white dark:bg-slate-800 shadow-lg scale-[1.02] border-l-4 border-primary dark:border-cyan'
            : 'hover:bg-teal/5 dark:hover:bg-light/5 text-darkTeal/60 dark:text-light/60 border-l-4 border-transparent'
            }`}
    >
        <div className={`p-3 rounded-lg transition-colors ${activeTab === id
            ? 'bg-primary/10 dark:bg-cyan/10 text-primary dark:text-cyan'
            : 'bg-transparent text-darkTeal/40 dark:text-light/40 group-hover:text-primary dark:group-hover:text-cyan'
            }`}>
            <span className="material-symbols-outlined text-2xl">{icon}</span>
        </div>
        <div>
            <span className={`block font-bold text-sm ${activeTab === id ? 'text-darkTeal dark:text-light' : ''}`}>
                {label}
            </span>
            <span className="text-xs opacity-70 hidden md:block">
                {description}
            </span>
        </div>
    </button>
);

const Settings = () => {
    const { user, updateUser } = useAuth();
    const [activeTab, setActiveTab] = useState('profile');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [toast, setToast] = useState(null);
    const [availableTimezones, setAvailableTimezones] = useState([]);

    // Profile State
    const [profileData, setProfileData] = useState({
        full_name: '',
        company_name: '',
        phone: '',
        timezone: 'GMT-05:00',
        default_country: 'United States',
        calendar_link: ''
    });

    // Notifications State
    const [notificationSettings, setNotificationSettings] = useState({
        email_notifications: true,
        sms_notifications: false,
        in_app_notifications: true,
        notification_email: '',
        notification_phone: '',
        notify_on_new_lead: true,
        notify_on_auto_respond: true,
        notify_on_reply: true,
        quiet_hours_start: '22:00',
        quiet_hours_end: '08:00',
        quiet_mode_enabled: false
    });

    // Fetch initial data
    useEffect(() => {
        fetchSettings();
    }, []);

    // Update available timezones when country changes
    useEffect(() => {
        const timezones = TIMEZONE_DATA[profileData.default_country] || [];
        setAvailableTimezones(timezones);

        // Reset timezone if current one isn't valid for the new country
        // unless it's the initial load or we want to preserve it if possible
        const isValidTimezone = timezones.some(tz => tz.value === profileData.timezone);
        if (!isValidTimezone && timezones.length > 0) {
            setProfileData(prev => ({ ...prev, timezone: timezones[0].value }));
        }
    }, [profileData.default_country]);

    // Auto-dismiss toast
    useEffect(() => {
        if (toast) {
            const timer = setTimeout(() => setToast(null), 3000);
            return () => clearTimeout(timer);
        }
    }, [toast]);

    const fetchSettings = async () => {
        try {
            setLoading(true);
            const [profileRes, notifRes] = await Promise.allSettled([
                settingsAPI.getProfile(),
                settingsAPI.getNotificationPreferences()
            ]);

            if (profileRes.status === 'fulfilled') {
                const data = profileRes.value.data?.user || {};
                setProfileData(prev => ({
                    ...prev,
                    full_name: data.full_name || '',
                    company_name: data.company_name || '',
                    phone: data.phone || '',
                    timezone: data.timezone || 'GMT-05:00',
                    default_country: data.default_country && TIMEZONE_DATA[data.default_country] ? data.default_country : 'United States',
                    calendar_link: data.calendar_link || '',
                    avatar_url: data.avatar_url || '',
                    channel_preferences: data.channel_preferences || { multi_channel: false, priorities: {} }
                }));
            }

            if (notifRes.status === 'fulfilled') {
                const data = notifRes.value.data?.preferences || {};
                setNotificationSettings({
                    email_notifications: data.email_notifications ?? true,
                    sms_notifications: data.sms_notifications ?? false,
                    in_app_notifications: data.in_app_notifications ?? true,
                    notification_email: data.notification_email || '',
                    notification_phone: data.notification_phone || '',
                    notify_on_new_lead: data.notify_on_new_lead ?? true,
                    notify_on_auto_respond: data.notify_on_auto_respond ?? true,
                    notify_on_reply: data.notify_on_reply ?? true,
                    quiet_hours_start: data.quiet_hours_start || '22:00',
                    quiet_hours_end: data.quiet_hours_end || '08:00',
                    quiet_mode_enabled: data.quiet_mode_enabled ?? false
                });
            }
        } catch (error) {
            console.error('Error fetching settings:', error);
            setToast({ type: 'error', text: 'Failed to load settings' });
        } finally {
            setLoading(false);
        }
    };

    const handleSaveProfile = async () => {
        try {
            setSaving(true);
            const res = await settingsAPI.updateProfile(profileData);

            // Update local user context if needed
            if (updateUser) {
                updateUser({ ...user, ...res.data.user });
            }

            setToast({ type: 'success', text: 'Profile updated successfully!' });
        } catch (error) {
            console.error('Error saving profile:', error);
            setToast({ type: 'error', text: 'Failed to update profile' });
        } finally {
            setSaving(false);
        }
    };

    const handleSaveNotifications = async () => {
        try {
            setSaving(true);
            await settingsAPI.updateNotificationPreferences(notificationSettings);
            setToast({ type: 'success', text: 'Notification preferences saved!' });
        } catch (error) {
            console.error('Error saving notifications:', error);
            setToast({ type: 'error', text: 'Failed to save preferences' });
        } finally {
            setSaving(false);
        }
    };

    const handleProfileChange = (e) => {
        const { name, value } = e.target;
        setProfileData(prev => ({ ...prev, [name]: value }));
    };

    const handleChannelPreferenceChange = (field, value) => {
        setProfileData(prev => ({
            ...prev,
            channel_preferences: {
                ...prev.channel_preferences,
                [field]: value
            }
        }));
    };

    const handleNotificationChange = (field, value) => {
        setNotificationSettings(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleAvatarUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Basic validation
        if (!file.type.startsWith('image/')) {
            setToast({ type: 'error', text: 'Please select an image file.' });
            return;
        }

        if (file.size > 5 * 1024 * 1024) { // 5MB
            setToast({ type: 'error', text: 'Image size should be less than 5MB.' });
            return;
        }

        try {
            setSaving(true);
            const res = await settingsAPI.uploadAvatar(file);

            // Update local state
            setProfileData(prev => ({ ...prev, avatar_url: res.data.avatar_url }));

            // Update context if available
            if (updateUser) {
                updateUser({ ...user, avatar_url: res.data.avatar_url });
            }

            setToast({ type: 'success', text: 'Profile photo updated!' });
        } catch (error) {
            console.error('Avatar upload error:', error);
            setToast({ type: 'error', text: 'Failed to upload photo.' });
        } finally {
            setSaving(false);
        }
    };

    // ─── Components moved outside to prevent re-renders ───

    if (loading) {
        return (
            <div className="min-h-screen p-8 animate-pulse flex gap-8">
                <div className="w-64 space-y-4">
                    <div className="h-20 bg-teal/10 dark:bg-light/10 rounded-xl"></div>
                    <div className="h-20 bg-teal/10 dark:bg-light/10 rounded-xl"></div>
                </div>
                <div className="flex-1 space-y-6">
                    <div className="h-32 bg-teal/10 dark:bg-light/10 rounded-xl"></div>
                    <div className="h-64 bg-teal/10 dark:bg-light/10 rounded-xl"></div>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-mint dark:bg-navy min-h-screen text-left">
            {/* Toast */}
            {toast && (
                <div className={`fixed bottom-10 right-10 z-50 px-6 py-4 rounded-xl shadow-[0_10px_40px_rgba(0,0,0,0.2)] text-sm font-bold flex items-center gap-3 animate-slide-up ${toast.type === 'success'
                    ? 'bg-navy text-cyan border border-cyan/30'
                    : 'bg-navy text-red-400 border border-red-500/30'
                    }`}>
                    <span className="material-symbols-outlined text-xl">
                        {toast.type === 'success' ? 'verified' : 'error'}
                    </span>
                    {toast.text}
                </div>
            )}

            {/* Header */}
            <div className="mb-8">
                <h2 className="text-2xl md:text-3xl font-bold text-darkTeal dark:text-light mb-2">Account Settings</h2>
                <p className="text-darkTeal/50 dark:text-light/50">Manage your personal details and system preferences.</p>
            </div>

            <div className="flex flex-col lg:flex-row gap-4 lg:gap-12 items-start">

                {/* ─── Sticky Sidebar Navigation ─── */}
                <aside className="w-full lg:w-72 flex-shrink-0 lg:sticky lg:top-8 space-y-2">
                    <NavItem
                        id="profile"
                        icon="badge"
                        label="My Profile"
                        description="Personal details & branding"
                        activeTab={activeTab}
                        setActiveTab={setActiveTab}
                    />
                    <NavItem
                        id="auto-response"
                        icon="alt_route"
                        label="Auto-Response"
                        description="Channel routing & logic"
                        activeTab={activeTab}
                        setActiveTab={setActiveTab}
                    />
                    <NavItem
                        id="notifications"
                        icon="notifications_active"
                        label="Notifications"
                        description="Email & SMS alerts"
                        activeTab={activeTab}
                        setActiveTab={setActiveTab}
                    />
                </aside>

                {/* ─── Main Content Area ─── */}
                <main className="flex-1 w-full min-h-[500px] animate-fade-in-up">

                    {/* PROFILE TAB */}
                    {activeTab === 'profile' && (
                        <div className="space-y-8">
                            {/* Hero Card / ID Card */}
                            <div className="bg-gradient-to-r from-primary/10 to-teal/10 dark:from-cyan/10 dark:to-blue-600/10 rounded-2xl p-6 border border-primary/20 dark:border-cyan/20 flex flex-col sm:flex-row items-center sm:items-start gap-6 relative overflow-hidden">
                                <div className="absolute top-0 right-0 p-32 bg-primary/20 dark:bg-cyan/20 blur-[80px] rounded-full pointer-events-none"></div>

                                <div className="relative group">
                                    <div className="w-24 h-24 rounded-2xl bg-white dark:bg-slate-800 shadow-lg flex items-center justify-center text-4xl font-bold text-primary dark:text-cyan border-2 border-white dark:border-navy ring-4 ring-primary/20 dark:ring-cyan/20 overflow-hidden">
                                        {profileData.avatar_url ? (
                                            <img
                                                src={profileData.avatar_url}
                                                alt="Profile"
                                                className="w-full h-full object-cover"
                                            />
                                        ) : (
                                            <span>{profileData.full_name ? profileData.full_name.charAt(0).toUpperCase() : 'U'}</span>
                                        )}
                                    </div>
                                    <label className="absolute -bottom-2 -right-2 bg-darkTeal dark:bg-white text-white dark:text-navy p-2 rounded-full shadow-lg hover:scale-110 transition-transform cursor-pointer">
                                        <span className="material-symbols-outlined text-sm font-bold">edit</span>
                                        <input
                                            type="file"
                                            className="hidden"
                                            accept="image/*"
                                            onChange={handleAvatarUpload}
                                        />
                                    </label>
                                </div>
                                <div className="text-center sm:text-left z-10">
                                    <h3 className="text-2xl font-bold text-darkTeal dark:text-light">{profileData.full_name || 'Your Name'}</h3>
                                    <p className="text-primary dark:text-cyan font-medium mb-1">{profileData.company_name || 'Company Name'}</p>
                                    <div className="flex flex-wrap justify-center sm:justify-start gap-2 mt-3">
                                        <span className="px-3 py-1 rounded-full bg-white/50 dark:bg-black/20 text-xs font-bold text-darkTeal/70 dark:text-light/70 border border-teal/10 dark:border-light/10 flex items-center gap-1">
                                            <span className="material-symbols-outlined text-xs">email</span> {user?.email}
                                        </span>
                                        <span className="px-3 py-1 rounded-full bg-white/50 dark:bg-black/20 text-xs font-bold text-darkTeal/70 dark:text-light/70 border border-teal/10 dark:border-light/10 flex items-center gap-1">
                                            <span className="material-symbols-outlined text-xs">public</span> {profileData.default_country}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <SettingsSection title="Personal Information" subtitle="Update your identity details.">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="group">
                                        <label className="block text-xs font-bold text-darkTeal/60 dark:text-light/50 uppercase tracking-wider mb-2 group-focus-within:text-primary dark:group-focus-within:text-cyan transition-colors">Full Name</label>
                                        <input
                                            type="text"
                                            name="full_name"
                                            value={profileData.full_name}
                                            onChange={handleProfileChange}
                                            className="w-full bg-white dark:bg-slate-800 border border-teal/10 dark:border-light/10 rounded-xl px-4 py-3 text-darkTeal dark:text-light focus:ring-2 focus:ring-primary/50 dark:focus:ring-cyan/50 focus:border-primary dark:focus:border-cyan outline-none transition-all shadow-sm relative z-10"
                                        />
                                    </div>
                                    <div className="group">
                                        <label className="block text-xs font-bold text-darkTeal/60 dark:text-light/50 uppercase tracking-wider mb-2 group-focus-within:text-primary dark:group-focus-within:text-cyan transition-colors">Company Name</label>
                                        <input
                                            type="text"
                                            name="company_name"
                                            value={profileData.company_name}
                                            onChange={handleProfileChange}
                                            className="w-full bg-white dark:bg-slate-800 border border-teal/10 dark:border-light/10 rounded-xl px-4 py-3 text-darkTeal dark:text-light focus:ring-2 focus:ring-primary/50 dark:focus:ring-cyan/50 focus:border-primary dark:focus:border-cyan outline-none transition-all shadow-sm relative z-10"
                                        />
                                    </div>
                                    <div className="group">
                                        <label className="block text-xs font-bold text-darkTeal/60 dark:text-light/50 uppercase tracking-wider mb-2 group-focus-within:text-primary dark:group-focus-within:text-cyan transition-colors">Phone Number</label>
                                        <input
                                            type="tel"
                                            name="phone"
                                            value={profileData.phone}
                                            onChange={handleProfileChange}
                                            className="w-full bg-white dark:bg-slate-800 border border-teal/10 dark:border-light/10 rounded-xl px-4 py-3 text-darkTeal dark:text-light focus:ring-2 focus:ring-primary/50 dark:focus:ring-cyan/50 focus:border-primary dark:focus:border-cyan outline-none transition-all shadow-sm relative z-10"
                                        />
                                    </div>
                                    <div className="group">
                                        <label className="block text-xs font-bold text-darkTeal/60 dark:text-light/50 uppercase tracking-wider mb-2 group-focus-within:text-primary dark:group-focus-within:text-cyan transition-colors">Country</label>
                                        <select
                                            name="default_country"
                                            value={profileData.default_country}
                                            onChange={handleProfileChange}
                                            className="w-full bg-white dark:bg-slate-800 border border-teal/10 dark:border-light/10 rounded-xl px-4 py-3 text-darkTeal dark:text-light focus:ring-2 focus:ring-primary/50 dark:focus:ring-cyan/50 focus:border-primary dark:focus:border-cyan outline-none transition-all shadow-sm cursor-pointer appearance-none relative z-10"
                                        >
                                            {COUNTRIES.map(country => (
                                                <option key={country} value={country}>{country}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="group md:col-span-2">
                                        <label className="block text-xs font-bold text-darkTeal/60 dark:text-light/50 uppercase tracking-wider mb-2 group-focus-within:text-primary dark:group-focus-within:text-cyan transition-colors">Time Zone</label>
                                        <select
                                            name="timezone"
                                            value={profileData.timezone}
                                            onChange={handleProfileChange}
                                            className="w-full bg-white dark:bg-slate-800 border border-teal/10 dark:border-light/10 rounded-xl px-4 py-3 text-darkTeal dark:text-light focus:ring-2 focus:ring-primary/50 dark:focus:ring-cyan/50 focus:border-primary dark:focus:border-cyan outline-none transition-all shadow-sm cursor-pointer appearance-none relative z-10"
                                        >
                                            {availableTimezones.map(tz => (
                                                <option key={tz.value} value={tz.value}>{tz.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </SettingsSection>

                            <SettingsSection title="Integrations" subtitle="Connect external tools.">
                                <div className="bg-white dark:bg-slate-800 border border-teal/10 dark:border-light/5 rounded-xl p-5 flex flex-col sm:flex-row items-center gap-4 sm:gap-4 shadow-sm hover:shadow-md transition-shadow">
                                    <div className="flex items-center gap-4 w-full sm:w-auto">
                                        <div className="w-12 h-12 rounded-full bg-blue-500/10 flex items-center justify-center text-blue-500 shrink-0">
                                            <span className="material-symbols-outlined">calendar_month</span>
                                        </div>
                                        <div>
                                            <p className="font-bold text-darkTeal dark:text-light">Booking Calendar</p>
                                            <p className="text-xs text-darkTeal/50 dark:text-light/50">Calendly, Cal.com, or Google Calendar</p>
                                        </div>
                                    </div>
                                    <div className="flex-1 w-full sm:max-w-xs sm:mx-4">
                                        <input
                                            type="url"
                                            name="calendar_link"
                                            value={profileData.calendar_link}
                                            onChange={handleProfileChange}
                                            placeholder="https://calendly.com/..."
                                            className="w-full bg-transparent border-b border-teal/20 dark:border-light/20 py-2 text-sm focus:border-primary dark:focus:border-cyan outline-none text-darkTeal dark:text-light text-left"
                                        />
                                    </div>
                                    <button
                                        onClick={() => window.open(profileData.calendar_link, '_blank')}
                                        disabled={!profileData.calendar_link}
                                        className="w-10 h-10 rounded-full hover:bg-teal/5 dark:hover:bg-light/10 flex items-center justify-center text-darkTeal/50 dark:text-light/50 hover:text-primary dark:hover:text-cyan transition-colors ml-auto sm:ml-0"
                                    >
                                        <span className="material-symbols-outlined">open_in_new</span>
                                    </button>
                                </div>
                            </SettingsSection>

                            <div className="flex justify-end pt-4 border-t border-teal/10 dark:border-light/10">
                                <button
                                    onClick={handleSaveProfile}
                                    disabled={saving}
                                    className="px-8 py-3 bg-darkTeal dark:bg-white hover:bg-primary dark:hover:bg-cyan text-white dark:text-navy font-bold rounded-xl shadow-lg hover:shadow-primary/30 dark:hover:shadow-cyan/30 transition-all flex items-center gap-2 transform active:scale-95"
                                >
                                    {saving ? (
                                        <span className="animate-spin material-symbols-outlined text-xl">progress_activity</span>
                                    ) : (
                                        <>
                                            <span>Save Changes</span>
                                            <span className="material-symbols-outlined text-xl">arrow_forward</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    )}

                    {/* AUTO-RESPONSE TAB */}
                    {activeTab === 'auto-response' && (
                        <div className="space-y-8">
                            <div className="bg-gradient-to-r from-blue-500/10 to-indigo-500/10 rounded-2xl p-6 border border-blue-500/10 flex flex-col md:flex-row items-center justify-between gap-6">
                                <div>
                                    <h3 className="text-xl font-bold text-darkTeal dark:text-light flex items-center gap-2">
                                        <span className="material-symbols-outlined text-blue-500">hub</span>
                                        Multi-Channel Mode
                                    </h3>
                                    <p className="text-darkTeal/60 dark:text-light/60 text-sm mt-1 max-w-md">
                                        When enabled, the auto-responder will attempt to message the lead on <strong>ALL</strong> available channels simultaneously (e.g., Email AND SMS).
                                        <br />
                                        <span className="text-xs opacity-70">If disabled, it stops after the first successful message.</span>
                                    </p>
                                </div>
                                <Toggle
                                    checked={profileData.channel_preferences?.multi_channel || false}
                                    onChange={(val) => handleChannelPreferenceChange('multi_channel', val)}
                                />
                            </div>

                            <SettingsSection title="Channel Priorities" subtitle="Order of preference for contacting leads.">
                                <div className="bg-white dark:bg-slate-800 border border-teal/10 dark:border-light/10 rounded-xl overflow-hidden p-6">
                                    <div className="text-sm text-darkTeal/60 dark:text-light/60 mb-6 bg-teal/5 dark:bg-light/5 p-3 rounded-lg border border-teal/5 flex items-start gap-2">
                                        <span className="material-symbols-outlined text-base mt-0.5 text-primary">info</span>
                                        <span>
                                            Priorities are automatically optimized based on your country settings (<strong>{profileData.default_country}</strong>).
                                        </span>
                                    </div>

                                    <div className="max-w-md mx-auto">
                                        {profileData.default_country === 'India' ? (
                                            <div>
                                                <h4 className="font-bold text-darkTeal dark:text-light mb-4 flex items-center justify-center gap-2 text-lg">
                                                    <span className="material-symbols-outlined text-orange-500">flag</span>
                                                    India Priority
                                                </h4>
                                                <ol className="list-decimal list-inside space-y-3 text-darkTeal/80 dark:text-light/80">
                                                    <li className="p-3 bg-teal/5 dark:bg-light/5 rounded-lg border border-teal/10 dark:border-light/10 font-bold text-green-600 dark:text-green-400 flex items-center justify-between shadow-sm">
                                                        <span>WhatsApp</span>
                                                        <span className="text-xs bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 px-2 py-1 rounded-full">Primary</span>
                                                    </li>
                                                    <li className="p-3 bg-white dark:bg-slate-700 rounded-lg border border-teal/10 dark:border-light/10 flex items-center justify-between">
                                                        <span>SMS</span>
                                                        <span className="text-xs opacity-60">Secondary</span>
                                                    </li>
                                                    <li className="p-3 bg-white dark:bg-slate-700 rounded-lg border border-teal/10 dark:border-light/10 flex items-center justify-between">
                                                        <span>Email</span>
                                                        <span className="text-xs opacity-60">Fallback</span>
                                                    </li>
                                                </ol>
                                            </div>
                                        ) : (
                                            <div>
                                                <h4 className="font-bold text-darkTeal dark:text-light mb-4 flex items-center justify-center gap-2 text-lg">
                                                    <span className="material-symbols-outlined text-blue-500">public</span>
                                                    Standard Priority
                                                </h4>
                                                <ol className="list-decimal list-inside space-y-3 text-darkTeal/80 dark:text-light/80">
                                                    <li className="p-3 bg-teal/5 dark:bg-light/5 rounded-lg border border-teal/10 dark:border-light/10 font-bold text-indigo-500 dark:text-indigo-400 flex items-center justify-between shadow-sm">
                                                        <span>Email</span>
                                                        <span className="text-xs bg-indigo-100 dark:bg-indigo-900 text-indigo-700 dark:text-indigo-300 px-2 py-1 rounded-full">Primary</span>
                                                    </li>
                                                    <li className="p-3 bg-white dark:bg-slate-700 rounded-lg border border-teal/10 dark:border-light/10 flex items-center justify-between">
                                                        <span>SMS</span>
                                                        <span className="text-xs opacity-60">Secondary</span>
                                                    </li>
                                                    <li className="p-3 bg-white dark:bg-slate-700 rounded-lg border border-teal/10 dark:border-light/10 text-darkTeal/60 dark:text-light/60 flex items-center justify-between">
                                                        <span>WhatsApp</span>
                                                        <span className="text-xs opacity-40 italic">If available</span>
                                                    </li>
                                                </ol>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </SettingsSection>

                            <div className="flex justify-end pt-4 border-t border-teal/10 dark:border-light/10">
                                <button
                                    onClick={handleSaveProfile}
                                    disabled={saving}
                                    className="px-8 py-3 bg-darkTeal dark:bg-white hover:bg-primary dark:hover:bg-cyan text-white dark:text-navy font-bold rounded-xl shadow-lg hover:shadow-primary/30 dark:hover:shadow-cyan/30 transition-all flex items-center gap-2 transform active:scale-95"
                                >
                                    {saving ? (
                                        <span className="animate-spin material-symbols-outlined text-xl">progress_activity</span>
                                    ) : (
                                        <>
                                            <span>Save Changes</span>
                                            <span className="material-symbols-outlined text-xl">save</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    )}

                    {/* NOTIFICATIONS TAB */}
                    {activeTab === 'notifications' && (
                        <div className="space-y-8">
                            <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-2xl p-6 border border-purple-500/10 flex flex-col md:flex-row items-center justify-between gap-6">
                                <div>
                                    <h3 className="text-xl font-bold text-darkTeal dark:text-light flex items-center gap-2">
                                        <span className="material-symbols-outlined text-purple-500">quiet_time</span>
                                        Quiet Mode
                                    </h3>
                                    <p className="text-darkTeal/60 dark:text-light/60 text-sm mt-1 max-w-md">
                                        Pause all notifications during specific hours. We'll queue them and send a summary when you're back.
                                    </p>
                                </div>
                                <div className="flex flex-col items-end gap-3">
                                    <Toggle
                                        checked={notificationSettings.quiet_mode_enabled}
                                        onChange={(val) => handleNotificationChange('quiet_mode_enabled', val)}
                                    />
                                    {notificationSettings.quiet_mode_enabled && (
                                        <div className="flex items-center gap-2 bg-white dark:bg-slate-800 p-2 rounded-lg shadow-sm animate-fade-in-up">
                                            <input
                                                type="time"
                                                value={notificationSettings.quiet_hours_start}
                                                onChange={(e) => handleNotificationChange('quiet_hours_start', e.target.value)}
                                                className="bg-transparent border-none text-sm font-bold text-darkTeal dark:text-light focus:ring-0 p-1"
                                            />
                                            <span className="text-darkTeal/30 dark:text-light/30">to</span>
                                            <input
                                                type="time"
                                                value={notificationSettings.quiet_hours_end}
                                                onChange={(e) => handleNotificationChange('quiet_hours_end', e.target.value)}
                                                className="bg-transparent border-none text-sm font-bold text-darkTeal dark:text-light focus:ring-0 p-1"
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>


                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="bg-white dark:bg-slate-800 border border-teal/10 dark:border-light/10 rounded-xl overflow-hidden shadow-sm">
                                    <div className="p-4 border-b border-teal/5 dark:border-light/5 bg-teal/5 dark:bg-light/5 flex items-center gap-3">
                                        <span className="material-symbols-outlined text-primary dark:text-cyan">mail</span>
                                        <h4 className="font-bold text-darkTeal dark:text-light">Email Alerts</h4>
                                    </div>
                                    <div className="p-4 space-y-4">
                                        {[
                                            { key: 'notify_on_new_lead', label: 'New Lead Received' },
                                            { key: 'notify_on_auto_respond', label: 'Auto-Response Sent' },
                                            { key: 'notify_on_reply', label: 'Lead Replies' }
                                        ].map(item => (
                                            <div key={item.key} className="flex items-center justify-between group">
                                                <span className="text-sm text-darkTeal/80 dark:text-light/80 font-medium group-hover:text-primary dark:group-hover:text-cyan transition-colors">{item.label}</span>
                                                <Toggle
                                                    checked={notificationSettings[item.key]}
                                                    onChange={(val) => handleNotificationChange(item.key, val)}
                                                />
                                            </div>
                                        ))}
                                        <div className="pt-4 mt-4 border-t border-teal/5 dark:border-light/5">
                                            <label className="block text-xs font-bold text-darkTeal/40 dark:text-light/40 uppercase mb-2">Send to email</label>
                                            <input
                                                type="email"
                                                value={notificationSettings.notification_email}
                                                onChange={(e) => handleNotificationChange('notification_email', e.target.value)}
                                                placeholder={user?.email}
                                                className="w-full bg-teal/5 dark:bg-light/5 border-none rounded-lg text-sm px-3 py-2 text-darkTeal dark:text-light"
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-white dark:bg-slate-800 border border-teal/10 dark:border-light/10 rounded-xl overflow-hidden shadow-sm">
                                    <div className="p-4 border-b border-teal/5 dark:border-light/5 bg-teal/5 dark:bg-light/5 flex items-center gap-3">
                                        <span className="material-symbols-outlined text-primary dark:text-cyan">sms</span>
                                        <h4 className="font-bold text-darkTeal dark:text-light">SMS Alerts</h4>
                                    </div>
                                    <div className="p-4 space-y-4">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <span className="block text-sm font-bold text-darkTeal dark:text-light">Enable SMS</span>
                                                <span className="text-xs text-darkTeal/50 dark:text-light/50">Carrier rates apply</span>
                                            </div>
                                            <Toggle
                                                checked={notificationSettings.sms_notifications}
                                                onChange={(val) => handleNotificationChange('sms_notifications', val)}
                                            />
                                        </div>
                                        {notificationSettings.sms_notifications && (
                                            <div className="animate-fade-in pt-2">
                                                <label className="block text-xs font-bold text-darkTeal/40 dark:text-light/40 uppercase mb-2">Mobile Number</label>
                                                <input
                                                    type="tel"
                                                    value={notificationSettings.notification_phone}
                                                    onChange={(e) => handleNotificationChange('notification_phone', e.target.value)}
                                                    placeholder="+1 (555) 000-0000"
                                                    className="w-full bg-teal/5 dark:bg-light/5 border-none rounded-lg text-sm px-3 py-2 text-darkTeal dark:text-light"
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="flex justify-end pt-4 border-t border-teal/10 dark:border-light/10">
                                <button
                                    onClick={handleSaveNotifications}
                                    disabled={saving}
                                    className="px-8 py-3 bg-darkTeal dark:bg-white hover:bg-primary dark:hover:bg-cyan text-white dark:text-navy font-bold rounded-xl shadow-lg hover:shadow-primary/30 dark:hover:shadow-cyan/30 transition-all flex items-center gap-2 transform active:scale-95"
                                >
                                    {saving ? (
                                        <span className="animate-spin material-symbols-outlined text-xl">progress_activity</span>
                                    ) : (
                                        <>
                                            <span>Save Preferences</span>
                                            <span className="material-symbols-outlined text-xl">check</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    )}
                </main>
            </div>
        </div>
    );
};

export default Settings;
